import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPut, endpoints } from "../api/client";
import type { MerchantSettings } from "../api/types";

export function useSettings() {
  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<MerchantSettings>(endpoints.settings);
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = useCallback(
    async (patch: Partial<MerchantSettings>) => {
      if (!settings) return;
      setSaving(true);
      setError(null);
      const updated = { ...settings, ...patch };
      try {
        await apiPut(endpoints.settings, updated);
        setSettings(updated);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save settings");
      } finally {
        setSaving(false);
      }
    },
    [settings],
  );

  return { settings, loading, saving, error, update, reload: load };
}
